import { useState, useEffect } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useForm, useFieldArray, useWatch, Control, UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Edit, Trash2, Search, Package, Image as ImageIcon, Box, Layers, Loader2, AlertCircle, X, Globe, Eye, ChevronLeft, ChevronRight, ImageDown } from 'lucide-react'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { useProducts } from '@/hooks/use-products'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { RichTextEditor } from '@/components/rich-text-editor'
import { Product, Category, type LocalizedString, productService } from '@/services/product-service'
import { MediaPicker } from '@/components/media/media-picker'
import { getMediaUrl } from '@/utils/url-utils'
import { toast } from 'sonner'
import { RefreshCw } from 'lucide-react'

const getLocalizedText = (val: string | LocalizedString | undefined): string => {
  if (!val) return ''
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val)
      return parsed?.tr || parsed?.en || val
    } catch {
      return val
    }
  }
  return val?.tr || val?.en || ''
}

const parseLocalizedObj = (val: string | LocalizedString | undefined): LocalizedString => {
  if (!val) return { tr: '' }
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val)
      return typeof parsed === 'object' && parsed !== null ? parsed : { tr: val }
    } catch {
      return { tr: val }
    }
  }
  return val as LocalizedString
}

const SUPPORTED_LANGUAGES = [
  { code: 'tr', name: 'Türkçe' },
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'ru', name: 'Русский' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'ar', name: 'العربية' },
]

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
]

const localizedStringSchema = z.object({
  tr: z.string().optional().or(z.literal('')),
  en: z.string().optional(),
  fr: z.string().optional(),
  es: z.string().optional(),
  ru: z.string().optional(),
  nl: z.string().optional(),
  ar: z.string().optional(),
})

const requiredLocalizedStringSchema = z.object({
  tr: z.string().min(2, { message: 'En az 2 karakter olmalıdır' }),
  en: z.string().optional(),
  fr: z.string().optional(),
  es: z.string().optional(),
  ru: z.string().optional(),
  nl: z.string().optional(),
  ar: z.string().optional(),
})

const productSchema = z.object({
  title: requiredLocalizedStringSchema,
  slug: requiredLocalizedStringSchema,
  description: requiredLocalizedStringSchema,
  details: localizedStringSchema.optional(),
  categoryAssignments: z.array(z.object({
    mainCategoryId: z.string().min(1, { message: 'Ana kategori seçmelisiniz' }),
    subCategoryId: z.string().optional()
  })).min(1, { message: 'En az bir kategori eklemelisiniz' }),
  externalUrl: z.string().optional().or(z.literal('')),
  modelUrl: z.string().optional().or(z.literal('')),
  isActive: z.boolean().default(true),
  orderIndex: z.coerce.number().default(0),
  imageFiles: z.unknown().optional(),
  modelFile: z.unknown().optional(),
  gallery: z.array(z.object({
    imageUrl: z.string(),
    altText: z.string().optional()
  })).optional(),
  seo: z.object({
    meta_title: localizedStringSchema.optional(),
    meta_description: localizedStringSchema.optional(),
    meta_keywords: localizedStringSchema.optional(),
  }).optional(),
})

type ProductFormValues = z.infer<typeof productSchema>

// --------------------------------------------------------------------------
// ISOLATED SUBCATEGORY ROW TO PREVENT RENDER LOOPS
// --------------------------------------------------------------------------
function CategoryAssignmentRow({
  index,
  control,
  form,
  categories,
  onRemove,
  showRemove
}: {
  index: number
  control: Control<ProductFormValues>
  form: UseFormReturn<ProductFormValues>
  categories: Category[]
  onRemove: () => void
  showRemove: boolean
}) {
  const mainCategoryId = useWatch({
    control,
    name: `categoryAssignments.${index}.mainCategoryId`,
  })

  // Sadece ana kategorisi olmayanlar (Kök kategoriler)
  const mainCategories = categories.filter(c => !c.parentIds || c.parentIds.length === 0)

  // Seçilen ana kategorinin alt kategorileri
  const subCategories = categories.filter(c => c.parentIds && c.parentIds.includes(mainCategoryId))

  return (
    <div className="relative grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end border-b pb-4 mb-4 last:border-0 last:pb-0 last:mb-0">
      <FormField
        control={control}
        name={`categoryAssignments.${index}.mainCategoryId`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Ana Kategori</FormLabel>
            <Select
              onValueChange={(val) => {
                field.onChange(val)
                form.setValue(`categoryAssignments.${index}.subCategoryId`, '') // reset subcat on main cat change
              }}
              value={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Ana Kategori seçin" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {mainCategories.map(category => (
                  <SelectItem key={category._id || category.id} value={category._id || category.id}>
                    {getLocalizedText(category.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`categoryAssignments.${index}.subCategoryId`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Alt Kategori {subCategories.length > 0 && <span className="text-red-500">*</span>}</FormLabel>
            <Select onValueChange={field.onChange} value={field.value} disabled={subCategories.length === 0}>
              <FormControl>
                <SelectTrigger className={subCategories.length > 0 ? "border-primary/20 bg-primary/5" : "bg-muted text-muted-foreground"}>
                  <SelectValue placeholder={subCategories.length > 0 ? "Alt Kategori seçin" : "Kategori Seçilemez"} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {subCategories.map(category => (
                  <SelectItem key={category._id || category.id} value={category._id || category.id}>
                    {getLocalizedText(category.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {showRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-destructive hover:bg-destructive/10 -mt-1"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}

function UrunlerPage() {
  const { products, isLoading, isError, categories, createProduct, updateProduct, deleteProduct } = useProducts()

  const [isAddEditOpen, setIsAddEditOpen] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [productToDelete, setProductToDelete] = useState<Product | null>(null)
  const [productToPreview, setProductToPreview] = useState<Product | null>(null)
  const [previewImageIndex, setPreviewImageIndex] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isTranslating, setIsTranslating] = useState(false)

  const [searchQuery, setSearchQuery] = useState('')
  const [mainCategoryFilter, setMainCategoryFilter] = useState('all')
  const [subCategoryFilter, setSubCategoryFilter] = useState('all')
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false)
  const [isModelPickerOpen, setIsModelPickerOpen] = useState(false)

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema) as any,
    defaultValues: {
      title: { tr: '' },
      slug: { tr: '' },
      description: { tr: '' },
      details: { tr: '' },
      categoryAssignments: [{ mainCategoryId: '', subCategoryId: '' }],
      externalUrl: '',
      modelUrl: '',
      isActive: true,
      orderIndex: 0,
      modelFile: null,
      gallery: [],
      seo: {
        meta_title: { tr: '' },
        meta_description: { tr: '' },
        meta_keywords: { tr: '' },
      }
    },
  })

  const { fields: categoryAssignmentFields, append: appendCategoryAssignment, remove: removeCategoryAssignment } = useFieldArray({
    control: form.control,
    name: 'categoryAssignments' as const,
  })

  useEffect(() => {
    if (selectedProduct) {
      // Çözümle: Gelen categoryIds listesini UI için eşleştirmelere çevir
      // Çözümle: Gelen categoryIds listesini UI için eşleştirmelere çevir
      const incomingIds = selectedProduct.categoryIds || [];
      const mainCatIds = incomingIds.filter(id => {
        const cat = categories.find(c => (c._id || c.id) === id);
        return cat && (!cat.parentIds || cat.parentIds.length === 0);
      });
      const subCatIds = incomingIds.filter(id => {
        const cat = categories.find(c => (c._id || c.id) === id);
        return cat && (cat.parentIds && cat.parentIds.length > 0);
      });

      const tempAssignments: { mainCategoryId: string; subCategoryId?: string }[] = [];
      const usedMains = new Set<string>();
      const usedSubs = new Set<string>();

      // Optimizasyon: En az alternatifi olan (en kısıtlayıcı) alt kategorileri önce eşleştir
      const subCatIdsSorted = [...subCatIds].sort((a, b) => {
        const catA = categories.find(c => (c._id || c.id) === a);
        const catB = categories.find(c => (c._id || c.id) === b);
        const aMains = mainCatIds.filter(m => catA?.parentIds?.includes(m)).length;
        const bMains = mainCatIds.filter(m => catB?.parentIds?.includes(m)).length;
        return aMains - bMains;
      });

      // Adım 1: Her alt kategori (subCat) için uygun ve BOŞTA OLAN bir ana kategori (mainCat) bul
      subCatIdsSorted.forEach(subId => {
        const subCat = categories.find(c => (c._id || c.id) === subId);
        if (!subCat) return;

        // Henüz kullanılmamış ve bu subCat'in parent'ı olan mainCat'leri bul
        // En az alt kategoriye sahip olan mainCat'i önce tercih et (picky first)
        const availableMains = mainCatIds
          .filter(m => subCat.parentIds?.includes(m) && !usedMains.has(m))
          .sort((a, b) => {
            const aSubs = subCatIds.filter(s => categories.find(c => (c._id || c.id) === s)?.parentIds?.includes(a)).length;
            const bSubs = subCatIds.filter(s => categories.find(c => (c._id || c.id) === s)?.parentIds?.includes(b)).length;
            return aSubs - bSubs;
          });

        if (availableMains.length > 0) {
          const chosenMain = availableMains[0];
          tempAssignments.push({ mainCategoryId: chosenMain, subCategoryId: subId });
          usedMains.add(chosenMain);
          usedSubs.add(subId);
        }
      });

      // Adım 2: Eşleşemeyen subCat'ler için (bütün uygun mainCat'ler kullanılmışsa), kullanılmış olan bir mainCat'e bağla VEYA yepyeni bir orphan row oluştur
      subCatIds.forEach(subId => {
        if (!usedSubs.has(subId)) {
          const subCat = categories.find(c => (c._id || c.id) === subId);
          if (!subCat) return;

          // Bu subCat'in herhangi bir parent'ı (kullanılmış da olsa) var mı mainCatIds içinde?
          const fallbackMain = mainCatIds.find(m => subCat.parentIds?.includes(m));

          if (fallbackMain) {
            tempAssignments.push({ mainCategoryId: fallbackMain, subCategoryId: subId });
          } else {
            // Hiç eşleşen parent yoksa veri tabanındaki orijinal parent'ı kullan (Orphan)
            const rootParent = subCat.parentIds?.[0] || '';
            if (rootParent) {
              tempAssignments.push({ mainCategoryId: rootParent, subCategoryId: subId });
            }
          }
        }
      });

      // Adım 3: Hiç alt kategorisi olmayan veya alt kategorisi kalmayan ana kategoriler için boş satır veya ortak alt kategori ekle
      mainCatIds.forEach(mainId => {
        if (!usedMains.has(mainId)) {
          // Bu ana kategoriye uyan herhangi bir subCat var mı (kullanılmışlar dahil)? Eğer uyan varsa o subCat'i kopyala.
          const compatibleSub = subCatIds.find(subId => {
            const subCat = categories.find(c => (c._id || c.id) === subId);
            return subCat?.parentIds?.includes(mainId);
          });

          if (compatibleSub) {
            tempAssignments.push({ mainCategoryId: mainId, subCategoryId: compatibleSub });
          } else {
            // Uyan hiçbir subCat yok. Demek ki gerçekten alt kategorisiz seçilmiş.
            tempAssignments.push({ mainCategoryId: mainId, subCategoryId: '' });
          }
        }
      });

      const assignments = tempAssignments;

      // Eğer boş gelirse en az 1 tane boş satır koy
      if (assignments.length === 0) {
        assignments.push({ mainCategoryId: '', subCategoryId: '' })
      }

      form.reset({
        title: parseLocalizedObj(selectedProduct.title),
        slug: parseLocalizedObj(selectedProduct.slug),
        description: parseLocalizedObj(selectedProduct.description),
        details: parseLocalizedObj(selectedProduct.details),
        categoryAssignments: assignments,
        externalUrl: selectedProduct.externalUrl || '',
        modelUrl: selectedProduct.modelUrl || '',
        isActive: selectedProduct.isActive ?? true,
        orderIndex: selectedProduct.orderIndex || 0,
        gallery: selectedProduct.gallery || [],
        seo: {
          meta_title: selectedProduct.seo?.meta_title ? parseLocalizedObj(selectedProduct.seo.meta_title) : { tr: '' },
          meta_description: selectedProduct.seo?.meta_description ? parseLocalizedObj(selectedProduct.seo.meta_description) : { tr: '' },
          meta_keywords: selectedProduct.seo?.meta_keywords ? parseLocalizedObj(selectedProduct.seo.meta_keywords) : { tr: '' },
        }
      })
    } else {
      // Sadece form dirty ise veya submit edildiyse resetle. İntialization loop'a girmemeli.
      if (form.formState.isDirty || form.formState.isSubmitSuccessful) {
        form.reset({
          title: { tr: '' },
          slug: { tr: '' },
          description: { tr: '' },
          details: { tr: '' },
          categoryAssignments: [{ mainCategoryId: '', subCategoryId: '' }],
          externalUrl: '',
          modelUrl: '',
          isActive: true,
          orderIndex: 0,
          gallery: [],
          seo: {
            meta_title: { tr: '' },
            meta_description: { tr: '' },
            meta_keywords: { tr: '' },
          }
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct, categories])

  // Simple slug generation
  const generateSlug = (text: string) => {
    return text
      .trim()
      .replace(/İ/g, 'i')
      .replace(/I/g, 'i')
      .replace(/ı/g, 'i')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    form.setValue('title.tr', value)
    if (!selectedProduct) {
      form.setValue('slug.tr', generateSlug(value))
    }
  }

  const onSubmit = async (values: ProductFormValues) => {
    setIsSubmitting(true)
    try {
      const finalValues = { ...values }

      // Upload Images
      const imageFiles = values.imageFiles as FileList | null
      let newGallery = [...(values.gallery || [])]

      if (imageFiles && imageFiles.length > 0) {
        const uploadRes = await productService.uploadProductImages(Array.from(imageFiles))
        if (uploadRes.images && uploadRes.images.length > 0) {
          const uploadedImages = uploadRes.images.map(img => ({
            imageUrl: img.url,
            altText: finalValues.title.tr || 'Ürün Görseli'
          }))
          newGallery = [...newGallery, ...uploadedImages].slice(0, 6)
        }
      }
      finalValues.gallery = newGallery

      // Upload Model
      const modelFile = values.modelFile as FileList | null
      if (modelFile && modelFile[0]) {
        const uploadRes = await productService.uploadProductModel(modelFile[0])
        finalValues.modelUrl = uploadRes.model.url
      } else if (!values.modelUrl) {
        finalValues.modelUrl = ''
      }

      // Remove additional UI-only fields
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { imageFiles: _, modelFile: __, categoryAssignments, categoryId: ___, ...rest } = finalValues as any

      // Check if subcategories are required for each assignment
      let hasValidationError = false;
      const flatCategoryIds: string[] = [];

      categoryAssignments.forEach((assignment: any, index: number) => {
        const { mainCategoryId, subCategoryId } = assignment;
        const selectedMainCat = categories.find(c => (c._id || c.id) === mainCategoryId);
        const catHasSubCategories = categories.some(cat => cat.parentIds?.includes(selectedMainCat?._id || selectedMainCat?.id || ''));

        if (catHasSubCategories && !subCategoryId) {
          form.setError(`categoryAssignments.${index}.subCategoryId` as any, {
            type: 'manual',
            message: 'Lütfen bir alt kategori seçiniz.',
          });
          hasValidationError = true;
        }

        if (mainCategoryId) {
          flatCategoryIds.push(mainCategoryId);
        }
        if (subCategoryId) {
          flatCategoryIds.push(subCategoryId);
        }
      });

      if (hasValidationError) {
        setIsSubmitting(false)
        return
      }

      // Benzersiz ID'leri alalım ki aynı kategori iki kere eklenmesin
      const uniqueCategoryIds = Array.from(new Set(flatCategoryIds));

      if (uniqueCategoryIds.length === 0) {
        form.setError('categoryAssignments' as any, {
          type: 'manual',
          message: 'En az bir kategori eşleşmesi tamamlanmalıdır.'
        });
        setIsSubmitting(false)
        return
      }

      const submitData = {
        ...rest,
        categoryIds: uniqueCategoryIds,
        title: JSON.stringify(finalValues.title),
        slug: JSON.stringify(finalValues.slug),
        description: JSON.stringify(finalValues.description),
        details: JSON.stringify(finalValues.details || { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' }),
        gallery: finalValues.gallery,
        seo: finalValues.seo
      }

      if (selectedProduct) {
        await updateProduct({ id: selectedProduct._id || selectedProduct.id, data: submitData })
      } else {
        await createProduct(submitData)
      }
      setIsAddEditOpen(false)
      setSelectedProduct(null)
      form.reset()
    } catch (error: any) {
      // Mutations already handle their own toasts, but upload errors might not
      if (!error?.response?.data?.message && !error?.response?.data?.errors) {
        import('@/utils/handle-server-error').then(({ handleServerError }) => {
          handleServerError(error)
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBulkTranslate = async () => {
    if (!confirm('Tüm ürünler için eksik çeviriler AI ile tamamlanacak. Bu işlem ürün sayısına göre vakit alabilir. Emin misiniz?')) return

    setIsTranslating(true)
    try {
      const res = await productService.bulkTranslateProducts()
      toast.success(res.message || 'Toplu çeviri başarıyla tamamlandı')
      window.location.reload()
    } catch (error) {
      console.error('Bulk translate error:', error)
      toast.error('Toplu çeviri sırasında bir hata oluştu')
    } finally {
      setIsTranslating(false)
    }
  }

  const handleDelete = async () => {
    if (productToDelete) {
      try {
        await deleteProduct(productToDelete._id || productToDelete.id)
        setIsDeleteConfirmOpen(false)
        setProductToDelete(null)
      } catch (_error) {
        // Error handled by mutation toast
      }
    }
  }

  const filteredProducts = products.filter(product => {
    const titleText = getLocalizedText(product.title).toLowerCase()
    const slugText = getLocalizedText(product.slug).toLowerCase()
    const matchesSearch = titleText.includes(searchQuery.toLowerCase()) ||
      slugText.includes(searchQuery.toLowerCase())

    let matchesCategory = true;

    if (mainCategoryFilter !== 'all') {
      // If a main category is selected, the product must have this main category OR one of its subcategories
      const mainCatSubCatIds = categories
        .filter(c => c.parentIds?.includes(mainCategoryFilter))
        .map(c => c._id || c.id);

      const hasMainCat = product.categoryIds?.includes(mainCategoryFilter);
      const hasSubCatOfMain = product.categoryIds?.some((id: string) => mainCatSubCatIds.includes(id));

      if (!hasMainCat && !hasSubCatOfMain) {
        matchesCategory = false;
      }

      // If a subcategory is ALSO selected, the product MUST have this specific subcategory
      if (matchesCategory && subCategoryFilter !== 'all' && !product.categoryIds?.includes(subCategoryFilter)) {
        matchesCategory = false;
      }
    }

    return matchesSearch && matchesCategory
  })

  // Derive filter dropdown options
  const mainCategories = categories.filter(c => !c.parentIds || c.parentIds.length === 0);
  const filterSubCategories = mainCategoryFilter === 'all'
    ? []
    : categories.filter(c => c.parentIds?.includes(mainCategoryFilter));

  const getCategoryName = (id: string) => {
    const cat = categories.find(c => (c._id || c.id) === id)
    return cat ? getLocalizedText(cat.name) : id
  }

  if (isError) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex flex-col items-center justify-center min-h-[400px] text-destructive">
            <AlertCircle className="h-8 w-8 mb-4" />
            <p className="font-semibold">Ürünler yüklenirken bir hata oluştu.</p>
            <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
              Tekrar Dene
            </Button>
          </div>
        </Main>
      </>
    )
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[60vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Ürünler</h1>
            <p className="text-sm text-muted-foreground">Ürünlerinizi ve stoklarınızı yönetin</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleBulkTranslate}
              disabled={isTranslating}
              className="border-primary/20 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
            >
              {isTranslating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Ürünleri Çevir
            </Button>
            <Button onClick={() => {
              setSelectedProduct(null)
              setIsAddEditOpen(true)
            }}>
              <Plus className="mr-2 h-4 w-4" />
              Yeni Ürün
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam Ürün</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{products.length}</div>
              <p className="text-xs text-muted-foreground">Sistemdeki toplam kayıt</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Aktif Ürünler</CardTitle>
              <Box className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{products.filter(p => p.isActive).length}</div>
              <p className="text-xs text-muted-foreground">Yayında olan ürünler</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Kategoriler</CardTitle>
              <Layers className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{categories.length}</div>
              <p className="text-xs text-muted-foreground">Tanımlı ürün kategorileri</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Ürün Listesi</CardTitle>
                <CardDescription>
                  {filteredProducts.length} ürün listeleniyor
                </CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-2">
                <Select
                  value={mainCategoryFilter}
                  onValueChange={(val) => {
                    setMainCategoryFilter(val)
                    setSubCategoryFilter('all') // Reset sub category when main changes
                  }}
                >
                  <SelectTrigger className="w-full sm:w-[160px] h-9">
                    <SelectValue placeholder="Ana Kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Kategoriler</SelectItem>
                    {mainCategories.map(category => (
                      <SelectItem key={category._id || category.id} value={category._id || category.id}>
                        {getLocalizedText(category.name)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={subCategoryFilter}
                  onValueChange={setSubCategoryFilter}
                  disabled={mainCategoryFilter === 'all' || filterSubCategories.length === 0}
                >
                  <SelectTrigger className="w-full sm:w-[160px] h-9">
                    <SelectValue placeholder="Alt Kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {filterSubCategories.map(category => (
                      <SelectItem key={category._id || category.id} value={category._id || category.id}>
                        {getLocalizedText(category.name)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="relative flex-1 w-full sm:min-w-[200px]">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Ürün ara..."
                    className="pl-8 h-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                <p className="text-sm text-muted-foreground">Ürünler yükleniyor...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Görsel</TableHead>
                      <TableHead>Ürün Adı</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Sıra</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.map((product) => (
                      <TableRow key={product._id || product.id}>
                        <TableCell>
                          {product.gallery && product.gallery.length > 0 ? (
                            <img src={getMediaUrl(product.gallery[0].imageUrl)} alt={getLocalizedText(product.title)} className="h-10 w-10 rounded-md object-cover border" />
                          ) : (
                            <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center border">
                              <ImageIcon className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            <span>{getLocalizedText(product.title) || 'İsimsiz'}</span>
                            <span className="text-[10px] text-muted-foreground">{getLocalizedText(product.slug)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {product.categoryIds?.map((id: string) => (
                              <Badge key={id} variant="outline" className="font-normal block w-max">
                                {getCategoryName(id)}
                              </Badge>
                            )) || <Badge variant="outline" className="font-normal">Kategori Yok</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={product.isActive ? 'default' : 'secondary'}>
                            {product.isActive ? 'Aktif' : 'Pasif'}
                          </Badge>
                        </TableCell>
                        <TableCell>{product.orderIndex}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {product.createdAt ? new Date(product.createdAt).toLocaleDateString('tr-TR') : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedProduct(product)
                                setIsAddEditOpen(true)
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setProductToPreview(product)
                                setPreviewImageIndex(0)
                                setIsPreviewOpen(true)
                              }}
                              title="Önizleme"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                setProductToDelete(product)
                                setIsDeleteConfirmOpen(true)
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredProducts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                          Ürün bulunamadı.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ürün Ekle/Düzenle Diyaloğu */}
        <Dialog open={isAddEditOpen} onOpenChange={setIsAddEditOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedProduct ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit, (errors) => {
                  console.error('Form Validation Errors:', errors);
                  toast.error('Lütfen tüm zorunlu alanları (Ad, Özet, Kategori vb.) doğru şekilde doldurun.');
                })}
                className="space-y-4 py-2"
              >
                <div className="relative w-full rounded-lg border px-4 py-3 text-sm flex items-start gap-3 bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300">
                  <Globe className="h-4 w-4 mt-0.5" />
                  <div>
                    <p className="font-medium mb-1 flex items-center gap-1">✨ Yapay Zeka Çevirisi Aktif</p>
                    Türkçe içerik (başlık, özet, detaylar ve SEO alanları) baz alınarak tüm diller otomatik olarak çevrilecektir. Maddelerin çokluğuna ve içeriğin uzunluğuna göre bu işlem biraz vakit alabilir, lütfen işlem tamamlanana kadar bekleyiniz.
                  </div>
                </div>

                {selectedProduct ? (
                  <Tabs defaultValue="tr" className="w-full">
                    <TabsList className="w-full justify-start overflow-x-auto rounded-none border-b bg-transparent p-0 mb-4 items-end custom-scrollbar">
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <TabsTrigger
                          key={lang.code}
                          value={lang.code}
                          className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none focus-visible:ring-0 data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
                        >
                          <Globe className="w-3.5 h-3.5 mr-2 opacity-70" />
                          {lang.name}
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <TabsContent key={lang.code} value={lang.code} className="space-y-4 focus-visible:outline-none focus-visible:ring-0 mt-0">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name={`title.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Ürün Adı ({lang.name})</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={lang.code === 'tr' ? "Örn: SKG-500 Ekskavatör" : `Title in ${lang.name}`}
                                    {...field}
                                    value={field.value || ''}
                                    onChange={(e) => {
                                      field.onChange(e)
                                      if (lang.code === 'tr') {
                                        handleTitleChange(e)
                                      }
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`slug.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Slug ({lang.name})</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={lang.code === 'tr' ? "örn: skg-500-ekskavator" : `slug-${lang.code}`}
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name={`description.${lang.code}` as any}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Özet Açıklama ({lang.name})</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={lang.code === 'tr' ? "Örn: Yüksek performanslı..." : `Description in ${lang.name}`}
                                  {...field}
                                  value={field.value || ''}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name={`details.${lang.code}` as any}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Detaylar ({lang.name})</FormLabel>
                              <FormControl>
                                <RichTextEditor
                                  key={`rte-${lang.code}`}
                                  value={field.value || ''}
                                  onChange={field.onChange as any}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="space-y-4 border rounded-md p-4 bg-muted/20 mt-4">
                          <h4 className="text-sm font-semibold flex items-center gap-2">
                            <Search className="h-4 w-4 text-primary" />
                            SEO Ayarları ({lang.name})
                          </h4>
                          <FormField
                            control={form.control}
                            name={`seo.meta_title.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Meta Başlık</FormLabel>
                                <FormControl>
                                  <Input placeholder="Boş bırakılırsa ürün adı kullanılır" {...field} value={field.value || ''} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`seo.meta_description.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Meta Açıklama</FormLabel>
                                <FormControl>
                                  <Input placeholder="Arama sonuçlarında görünecek açıklama" {...field} value={field.value || ''} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`seo.meta_keywords.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Meta Anahtar Kelimeler</FormLabel>
                                <FormControl>
                                  <Input placeholder="Virgülle ayırarak yazın (örn: matkap, inşaat)" {...field} value={field.value || ''} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="title.tr"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Ürün Adı</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Örn: SKG-500 Ekskavatör"
                                {...field}
                                value={field.value || ''}
                                onChange={(e) => {
                                  field.onChange(e)
                                  handleTitleChange(e)
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="slug.tr"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Slug (URL yolu)</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="örn: skg-500-ekskavator"
                                {...field}
                                value={field.value || ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="description.tr"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Özet Açıklama</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Örn: Yüksek performanslı..."
                              {...field}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="details.tr"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Detaylar</FormLabel>
                          <FormControl>
                            <RichTextEditor
                              value={field.value || ''}
                              onChange={field.onChange as any}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="space-y-4 border rounded-md p-4 bg-muted/20 mt-4">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <Search className="h-4 w-4 text-primary" />
                        SEO Ayarları (Türkçe)
                      </h4>
                      <FormField
                        control={form.control}
                        name="seo.meta_title.tr"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Meta Başlık</FormLabel>
                            <FormControl>
                              <Input placeholder="Boş bırakılırsa ürün adı kullanılır" {...field} value={field.value || ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="seo.meta_description.tr"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Meta Açıklama</FormLabel>
                            <FormControl>
                              <Input placeholder="Arama sonuçlarında görünecek açıklama" {...field} value={field.value || ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="seo.meta_keywords.tr"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Meta Anahtar Kelimeler</FormLabel>
                            <FormControl>
                              <Input placeholder="Virgülle ayırarak yazın (örn: matkap, inşaat)" {...field} value={field.value || ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}

                {/* Dynamic Categories Section */}
                <div className="space-y-4 border rounded-md p-4 bg-muted/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-sm">Kategoriler</h3>
                      <p className="text-[12px] text-muted-foreground">Ürünün listeleneceği kategori ve alt kategorileri seçin.</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => appendCategoryAssignment({ mainCategoryId: '', subCategoryId: '' })}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Kategori Ekle
                    </Button>
                  </div>
                  {categoryAssignmentFields.map((field, index) => (
                    <CategoryAssignmentRow
                      key={field.id}
                      index={index}
                      control={form.control}
                      form={form}
                      categories={categories}
                      onRemove={() => removeCategoryAssignment(index)}
                      showRemove={categoryAssignmentFields.length > 1}
                    />
                  ))}
                  <FormField
                    control={form.control}
                    name="categoryAssignments"
                    render={() => (
                      <FormItem>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="orderIndex"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sıralama</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <FormLabel>Ürün Görselleri</FormLabel>
                    <div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setIsMediaPickerOpen(true)}
                      >
                        <ImageIcon className="h-4 w-4 mr-2" />
                        Kütüphaneden Görsel Ekle
                      </Button>
                    </div>
                  </div>

                  {form.watch('gallery') && form.watch('gallery')!.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {form.watch('gallery')!.map((img, index) => (
                        <div key={index} className="relative aspect-square rounded-md overflow-hidden border group bg-muted">
                          <img
                            src={getMediaUrl(img.imageUrl)}
                            alt={`Gallery ${index}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const currentGallery = form.getValues('gallery') || []
                              form.setValue('gallery', currentGallery.filter((_, i) => i !== index))
                            }}
                            className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="externalUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dış Bağlantı (Opsiyonel)</FormLabel>
                        <FormControl>
                          <Input placeholder="https://..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="space-y-4">
                    <FormLabel>3D Model (.glb, .gltf, .obj, .stl)</FormLabel>
                    <div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setIsModelPickerOpen(true)}
                      >
                        <Box className="h-4 w-4 mr-2" />
                        Kütüphaneden 3D Model Seç
                      </Button>
                    </div>

                    {form.watch('modelUrl') && (
                      <div className="flex items-center justify-between p-2 border rounded-md bg-muted/50">
                        <div className="flex items-center gap-2 overflow-hidden">
                          <Box className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-xs truncate">{form.watch('modelUrl')?.split('/').pop() || form.watch('modelUrl')}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => {
                            form.setValue('modelUrl', '')
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                      <div className="space-y-0.5">
                        <FormLabel>Aktif</FormLabel>
                        <p className="text-[10px] text-muted-foreground">
                          Ürün sitede listelenecek.
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <DialogFooter className="pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsAddEditOpen(false)} disabled={isSubmitting}>
                    İptal
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Yapay zeka çevirileri yapılıyor, lütfen bekleyin...
                      </>
                    ) : selectedProduct ? (
                      'Güncelle'
                    ) : (
                      'Kaydet'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Ürün Önizleme Diyaloğu */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto w-[95vw] sm:w-full">
            <DialogHeader className="border-b pb-4 mt-2">
              <DialogTitle className="text-2xl font-bold flex flex-col gap-1">
                {productToPreview ? (parseLocalizedObj(productToPreview.title) as any).tr : 'Adsız Ürün'}
                {productToPreview?.categoryIds && productToPreview.categoryIds.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {productToPreview.categoryIds.map((id: string) => (
                      <span key={id} className="text-xs font-normal text-muted-foreground uppercase tracking-wider bg-muted/50 px-2 py-0.5 rounded border">
                        {getCategoryName(id)}
                      </span>
                    ))}
                  </div>
                )}
              </DialogTitle>
            </DialogHeader>

            {productToPreview && (
              <Tabs defaultValue="tr" className="w-full mt-2">
                <TabsList className="w-full justify-start overflow-x-auto rounded-none border-b bg-transparent p-0 mb-4 items-end custom-scrollbar">
                  {SUPPORTED_LANGUAGES.map(lang => (
                    <TabsTrigger
                      key={lang.code}
                      value={lang.code}
                      className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-6 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none focus-visible:ring-0 data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
                    >
                      <Globe className="w-4 h-4 mr-2 opacity-70" />
                      {lang.name}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {SUPPORTED_LANGUAGES.map(lang => {
                  const pDesc = parseLocalizedObj(productToPreview.description) as any;
                  const pDetails = parseLocalizedObj(productToPreview.details) as any;
                  const gallery = productToPreview.gallery || [];
                  const hasImages = gallery.length > 0;

                  return (
                    <TabsContent key={lang.code} value={lang.code} className="space-y-6 focus-visible:outline-none focus-visible:ring-0 mt-0">
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Sol Kolon - Görseller */}
                        <div className="lg:col-span-5 space-y-4">
                          <div className="relative aspect-square md:aspect-[4/3] rounded-xl overflow-hidden bg-muted border flex items-center justify-center group">
                            {hasImages ? (
                              <>
                                <img
                                  src={getMediaUrl(gallery[previewImageIndex].imageUrl)}
                                  alt={gallery[previewImageIndex].altText || `Ürün Görseli ${previewImageIndex + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                {gallery.length > 1 && (
                                  <>
                                    <Button
                                      variant="secondary"
                                      size="icon"
                                      className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full h-10 w-10 opacity-70 group-hover:opacity-100 transition-opacity drop-shadow-md"
                                      onClick={() => setPreviewImageIndex(prev => (prev > 0 ? prev - 1 : gallery.length - 1))}
                                    >
                                      <ChevronLeft className="h-6 w-6" />
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="icon"
                                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full h-10 w-10 opacity-70 group-hover:opacity-100 transition-opacity drop-shadow-md"
                                      onClick={() => setPreviewImageIndex(prev => (prev < gallery.length - 1 ? prev + 1 : 0))}
                                    >
                                      <ChevronRight className="h-6 w-6" />
                                    </Button>
                                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm">
                                      {previewImageIndex + 1} / {gallery.length}
                                    </div>
                                  </>
                                )}
                              </>
                            ) : (
                              <div className="flex flex-col items-center justify-center text-muted-foreground p-6 text-center h-full">
                                <ImageDown className="h-16 w-16 mb-4 opacity-30" />
                                <p>Bu ürün için görsel yüklenmemiş.</p>
                              </div>
                            )}
                          </div>

                          {/* Küçük Görseller (Thumbnail) Galeri */}
                          {gallery.length > 1 && (
                            <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
                              {gallery.map((img, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => setPreviewImageIndex(idx)}
                                  className={`relative flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all ${idx === previewImageIndex ? 'border-primary ring-2 ring-primary/30' : 'border-transparent opacity-60 hover:opacity-100'
                                    }`}
                                >
                                  <img src={getMediaUrl(img.imageUrl)} alt="" className="w-full h-full object-cover" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Sağ Kolon - Detaylar */}
                        <div className="lg:col-span-7 space-y-8 flex flex-col">
                          <div>
                            <h4 className="font-semibold text-muted-foreground text-sm uppercase tracking-wider mb-2 flex items-center gap-2">
                              Özet Açıklama
                              <div className="h-[1px] flex-1 bg-border/60"></div>
                            </h4>
                            <p className="text-foreground text-lg leading-relaxed bg-muted/40 p-5 rounded-lg border border-border/50">
                              {pDesc[lang.code] || pDesc.tr || <span className="text-muted-foreground italic">Özet açıklama bulunmuyor.</span>}
                            </p>
                          </div>

                          <div className="flex-1">
                            <h4 className="font-semibold text-muted-foreground text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                              Detaylı Bilgiler
                              <div className="h-[1px] flex-1 bg-border/60"></div>
                            </h4>
                            <div
                              className="prose prose-blue dark:prose-invert max-w-none bg-card p-6 rounded-xl border border-border min-h-[300px] shadow-sm"
                              dangerouslySetInnerHTML={{ __html: pDetails[lang.code] || pDetails.tr || '<p class="text-muted-foreground italic text-center py-10">Daha fazla detay girilmemiş.</p>' }}
                            />
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  )
                })}
              </Tabs>
            )}

            <DialogFooter className="mt-6 border-t pt-4">
              <Button type="button" variant="outline" onClick={() => setIsPreviewOpen(false)}>
                Kapat
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Silme Onay Diyaloğu */}
        <AlertDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Emin misiniz?</AlertDialogTitle>
              <AlertDialogDescription>
                <strong>{productToDelete?.title ? (typeof productToDelete.title === 'string' ? productToDelete.title : productToDelete.title.tr) : 'Bu'}</strong> isimli ürünü silmek istediğinize emin misiniz?
                Bu işlem geri alınamaz.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Sil
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <MediaPicker
          open={isMediaPickerOpen}
          onOpenChange={setIsMediaPickerOpen}
          onSelect={(url) => {
            const currentGallery = form.getValues('gallery') || []
            form.setValue('gallery', [...currentGallery, { imageUrl: url }])
          }}
          title="Ürün Görseli Seç"
        />

        <MediaPicker
          open={isModelPickerOpen}
          onOpenChange={setIsModelPickerOpen}
          onSelect={(url) => form.setValue('modelUrl', url)}
          title="3D Model Seç"
        />
      </Main>
    </>
  )
}

export const Route = createFileRoute('/_authenticated/urunler/')({
  component: UrunlerPage,
})
